/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
} from '@angular/core';
import * as echarts from 'echarts/core';
import {
  type GraphTooltipParams,
  InsightsDataService,
} from '../../insights-data.service';
import { CommonModule } from '@angular/common';
import { frequencyIntervalGraph } from '../../insights-data.interface';
import { AccessibilityService } from '../../../../../../core/services/accessibility.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-frequency-interval-graph',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './frequency-interval-graph.component.html',
})
export class FrequencyIntervalGraphComponent implements OnDestroy {
  @Output() chartImage = new EventEmitter<string>();
  @Output() allChartImage = new EventEmitter<string[]>();
  private readonly destroy$ = new Subject<void>();
  chartInstance!: echarts.ECharts;
  chartInstances: any[] = [];
  weekGraphData: any = [];
  renderedChartsCount = 0;
  allChartsRendered = false;
  totalCharts = 0;
  isShow = true;
  maxLength = 15;
  splitRange = 15;
  groupSize = 5;
  behaviorId = 0;
  private readonly printTextColor = '#000000';
  private currentGraphData: frequencyIntervalGraph | null = null;

  @Input() set graphData(freqDatasheetData: frequencyIntervalGraph) {
    this.currentGraphData = freqDatasheetData;
    this.behaviorId = freqDatasheetData.caseBehaviorId ?? 0;
    this.setGraphOptionsDefault(freqDatasheetData, false);
    this.setGraphOptionsDefault(freqDatasheetData, true);
  }

  constructor(
    private readonly elementRef: ElementRef,
    private readonly insightService: InsightsDataService,
    private readonly accessibilityService: AccessibilityService
  ) {
    this.accessibilityService.contrastMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.applyThemeToExistingCharts();
      });
  }

  setGraphOptionsDefault(
    freqDatasheetData: frequencyIntervalGraph,
    showDaysInterval: boolean
  ) {
    if (showDaysInterval) {
      this.renderPrintChart(freqDatasheetData);
      return;
    }

    this.renderMainChart(freqDatasheetData);
  }

  private renderPrintChart(freqDatasheetData: frequencyIntervalGraph): void {
    const { xLabel, frequencyData } = freqDatasheetData;
    this.chartInstances = [];
    this.weekGraphData = [];
    this.renderedChartsCount = 0;
    this.allChartsRendered = false;
    this.totalCharts = 1;
    const hasValidData = xLabel.some(date =>
      Object.values(frequencyData).some(
        timeSlotData => timeSlotData[date] !== null
      )
    );

    const weekData = {
      xLabel: xLabel,
      frequencyData: frequencyData,
    };

    this.weekGraphData.push(weekData);
    const graphOptions = this.setGraphOptions(weekData, true);

    setTimeout(() => {
      const chartDom = document.getElementById(
        `chart-container-interval-${this.behaviorId}-${0}`
      );

      if (!chartDom) {
        console.error(
          `Chart container not found: chart-container-interval-${this.behaviorId}-${0}`
        );
        return;
      }

      this.initializeChart(chartDom, graphOptions, hasValidData);
    }, 0);
  }

  private renderMainChart(freqDatasheetData: frequencyIntervalGraph): void {
    const chartOptions = this.setGraphOptions(freqDatasheetData);

    const datasheetDom = this.elementRef.nativeElement.querySelector(
      '#frequency-interval-datasheet'
    );
    if (datasheetDom) {
      const chartInstance = echarts.init(datasheetDom);
      this.chartInstance = chartInstance;
      chartInstance.setOption(chartOptions);
      chartInstance.on('finished', () => {
        this.emitChartAsImage();
      });
    }
  }
  initializeChart(chartDom: any, graphOptions: any, hasValidData: boolean) {
    const chartInstance = echarts.init(chartDom);
    chartInstance.setOption(graphOptions);

    chartInstance.on('finished', () => this.chartRendered());

    setTimeout(() => {
      this.chartRendered();
    }, 2000);

    if (hasValidData) {
      this.chartInstances.push(chartInstance);
    }
  }

  chartRendered() {
    this.renderedChartsCount++;

    if (this.renderedChartsCount === this.totalCharts) {
      this.allChartsRendered = true;
      this.exportAllGraphs();
    }
  }

  setGraphOptions(freqDatasheetData: frequencyIntervalGraph, forPrint = false) {
    const dates: string[] = freqDatasheetData.xLabel.map(label =>
      this.insightService.formatTimeTextTo12Hour(label)
    );
    const textColor = forPrint
      ? this.printTextColor
      : this.getThemeChartTextColor();
    const originalTimeSlots: string[] = Object.keys(
      freqDatasheetData.frequencyData
    );
    const timeSlots: string[] = originalTimeSlots.map(slot =>
      this.insightService.formatTimeTextTo12Hour(slot)
    );

    const series = originalTimeSlots.map((originalTimeSlot, index) => ({
      name: timeSlots[index],
      type: 'bar',
      data: dates.map((_, dateIndex) => {
        const originalDate = freqDatasheetData.xLabel[dateIndex];
        return (
          freqDatasheetData.frequencyData[originalTimeSlot]?.[originalDate] ??
          ''
        );
      }),
      label: { show: true },
      itemStyle: { borderRadius: [10, 10, 0, 0] },
    }));

    const dataZoomConfig = [
      {
        type: 'inside',
        xAxisIndex: 0,
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        moveOnMouseWheel: true,
        minValueSpan: null,
        maxValueSpan: null,
        start: 0,
        end: 100,
      },
    ];

    const datasheetGraphOption = {
      title: {
        text: 'Interval Frequency Graph',
        left: 'center',
        textStyle: { fontSize: 16, fontWeight: 'bold', color: textColor },
      },
      grid: { left: 45, right: 45, top: 50, bottom: 100, containLabel: true },
      legend: {
        bottom: 8,
        left: 'center',
        width: '90%',
        orient: 'horizontal',
        icon: 'circle',
        itemWidth: 8,
        itemHeight: 8,
        textStyle: { fontSize: 14, fontWeight: 400, color: textColor },
        pageTextStyle: { color: textColor },
        data: timeSlots,
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: GraphTooltipParams[]) =>
          this.insightService.formatAxisTooltipWithWeekday(params, 'long'),
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: {
          margin: 14,
          hideOverlap: true,
          color: textColor,
        },
      },
      yAxis: {
        type: 'value',
        name: 'Frequency',
        nameGap: 35,
        splitLine: { show: true },
        axisLine: { show: true },
        nameLocation: 'center',
        nameTextStyle: { padding: [0, 0, 8, 0], color: textColor },
        minInterval: 1,
        axisLabel: {
          color: textColor,
          formatter: (value: number) => Math.floor(value).toString(),
        },
      },
      series,
      dataZoom: dataZoomConfig,
      graphic: {
        elements: [
          {
            type: 'rect',
            left: 0,
            top: 0,
            shape: { width: 0, height: 0 },
            draggable: true,
            ondrag: (dx: number) => {
              const chartContainer = document.getElementById('chart-container');
              if (!chartContainer) return;
              const chartInstance = echarts.getInstanceByDom(chartContainer);
              if (!chartInstance) return;

              const option = chartInstance.getOption() as any;
              const zoomFactor = (dx / chartContainer.offsetWidth) * 20;
              let newStart = option['dataZoom'][0].start + zoomFactor;
              newStart = Math.max(0, Math.min(newStart, 90));
              const newEnd = Math.min(100, newStart + 10);

              chartInstance.setOption(
                {
                  dataZoom: [{ start: newStart, end: newEnd }],
                  animationDurationUpdate: 300,
                },
                false
              );
            },
          },
        ],
      },
    };
    return datasheetGraphOption;
  }

  private getThemeChartTextColor(): string {
    const colorValue = getComputedStyle(document.documentElement)
      .getPropertyValue('--gray-600')
      .trim();

    return colorValue ? `rgb(${colorValue})` : '#475467';
  }

  private applyThemeToExistingCharts(): void {
    const graphData = this.currentGraphData;
    if (!graphData) {
      return;
    }

    if (this.chartInstance) {
      this.chartInstance.setOption(this.setGraphOptions(graphData), true);
    }

    for (const chart of this.chartInstances) {
      chart.setOption(this.setGraphOptions(graphData, true), true);
    }
  }

  exportAllGraphs() {
    if (!this.allChartsRendered) {
      return;
    }

    const images: string[] = [];

    for (const chartInstance of this.chartInstances) {
      const base64Image =
        this.insightService.convertImagetoBase64(chartInstance);

      images.push(base64Image);
    }
    this.allChartImage.emit(images);
  }

  emitChartAsImage() {
    const chartImageBase64 = this.insightService.convertImagetoBase64(
      this.chartInstance
    );
    this.chartImage.emit(chartImageBase64);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
